import { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import { sp } from '@/theme/mui';

// components
import { Button } from '@/components/atoms/Button/Button';
import { Container } from '@/components/atoms/Container/Container';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Input } from '@/components/atoms/Input/Input';
import { Label } from '@/components/atoms/Label/Label';
import { Option } from '@/components/atoms/Select/Select';
import { RadioGroup } from '@/components/atoms/RadioGroup/RadioGroup';
import { TextArea } from '@/components/atoms/TextArea/TextArea';
import { ToggleSelect } from '@/components/atoms/ToggleSelect/ToggleSelect';

import { CareCircleMemberSelector } from '@/components/molecules/CareCircleMemberSelector/CareCircleMemberSelector';

// models
import { CareCircle, CareCircleMemberInvite, Message } from '@karehero/models';

export const BookingDetails = ({
  relationshipOptions,
  careCircle,
  onSubmit,
  onInvite,
  onBack,
}: BookingDetailsProps) => {
  // state
  const [eventDetails, setEventDetails] = useState<
    Partial<BookingDetailsState>
  >({
    callType: 'google-meet',
  });

  // methods
  const handleSubmit = useCallback(() => {
    if (!onSubmit) return;
    onSubmit({
      callType: eventDetails.callType || '',
      phoneNumber: eventDetails.phoneNumber || '',
      careCircleMembers: eventDetails.careCircleMembers || [],
      reasonForCall: eventDetails.reasonForCall || '',
      note: eventDetails.note || '',
    });
  }, [eventDetails, onSubmit]);

  return (
    <Container size='md'>
      {onBack && (
        <Button
          ariaLabel='back'
          iconLeft='chevron-left'
          variant='secondary'
          size='sm'
          onPress={onBack}
        >
          Back
        </Button>
      )}
      <SubContainer>
        <ToggleSelect
          id='call-type'
          label='Select call type'
          isRequired
          options={[
            { label: 'Google meet', value: 'google-meet', icon: 'video' },
            { label: 'Phone call', value: 'phone-call', icon: 'phone' },
          ]}
          onChange={(value: string) => {
            setEventDetails((prev) => ({ ...prev, callType: value }));
          }}
          value={eventDetails.callType}
        />
      </SubContainer>
      <SubContainer>
        <Input
          label='Phone number'
          id='phone-number'
          placeholder='Phone number'
          isRequired
          size='md'
          onChange={(value: string) => {
            setEventDetails((prev) => ({ ...prev, phoneNumber: value }));
          }}
          value={eventDetails.phoneNumber || ''}
        />
      </SubContainer>
      {careCircle && (
        <SubContainer>
          <CareCircleMemberSelector
            id='care-circle-member-selector'
            label='Care circle members'
            description='Select the care circle members you want to invite to this call'
            maxColumns={1}
            relationshipOptions={relationshipOptions}
            careCircle={careCircle}
            onInvite={onInvite}
            value={eventDetails.careCircleMembers?.join(',')}
            onChange={(value: string) => {
              setEventDetails((prev) => ({
                ...prev,
                careCircleMembers: value.split(','),
              }));
            }}
          />
        </SubContainer>
      )}
      <SubContainer>
        <SubHeadingContainer>
          <Label text='Reason for call' id='reason-for-call' />
        </SubHeadingContainer>
        <RadioGroup
          options={[
            { label: 'Help to find care', value: '1' },
            { label: 'Help to fund care', value: '2' },
            {
              label: 'Understanding my loved one’s care needs',
              value: '3',
            },
            {
              label: 'Information around hospital discharge',
              value: '4',
            },
            {
              label: 'Help with respite care',
              value: '5',
            },
            {
              label: 'Help with power of attorney',
              value: '6',
            },
            { label: "I don’t know/I'm not sure", value: '7' },
          ]}
          variant='default'
          maxColumns={1}
          onChange={(value: string) => {
            setEventDetails((prev) => ({ ...prev, reasonForCall: value }));
          }}
          value={eventDetails.reasonForCall}
        />
      </SubContainer>
      <SubContainer>
        <TextArea
          label='Please share anything else that will help prepare for our meeting'
          id='note'
          placeholder='Write a note'
          onChange={(value: string) => {
            setEventDetails((prev) => ({ ...prev, note: value }));
          }}
          value={eventDetails.note}
        />
      </SubContainer>
      <Button
        ariaLabel='Book a call'
        variant='primary'
        onPress={handleSubmit}
        isFullWidth
      >
        Book a call
      </Button>
    </Container>
  );
};

const SubContainer = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ mb: sp[24] }}>{children}</Box>
);

const SubHeadingContainer = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', pb: sp[8] }}>
    {children}
  </Box>
);

export interface BookingDetailsState {
  callType: string;
  phoneNumber: string;
  careCircleMembers: string[];
  reasonForCall: string;
  note: string;
}

interface BookingDetailsProps {
  careCircle: CareCircle;
  relationshipOptions: Option[];
  onInvite: (
    careCircleMemberInvite: CareCircleMemberInvite,
  ) => Promise<Message | undefined>;
  onSubmit?: (details: BookingDetailsState) => void;
  onBack?: () => void;
}

BookingDetails.displayName = 'BookingDetails';

export default BookingDetails;
